import { z } from "zod";

import { batch } from "~/tools/impulse";
import {
	type GetImpulseFormOutput,
	type GetImpulseFormOutputVerbose,
	ImpulseFormList,
	ImpulseFormShape,
	ImpulseFormSwitch,
	ImpulseFormUnit,
} from "~/tools/impulse-form";
import { setFormElementsInput } from "~/tools/impulse-form/set-form-elements-input";
import { isPresent } from "~/tools/is-present";
import { isStringDirty } from "~/tools/is-string-dirty";
import { Template } from "~/tools/template";

import { DashboardWidgetSpec } from "../../common/dashboard-spec";

import { FormState as LinkFormState } from "./_link/form-state";
import { FormState as QueryFormState } from "./_query/form-state";
import { FormState as SettingsFormState } from "./_settings/form-state";
import type { FormProcessor } from "./_form-processor";

type Spec = DashboardWidgetSpec;

function specToInput(spec: Spec) {
	return {
		name: spec.name.source,
		description: spec.description.source,

		links: spec.plugin.links.map(LinkFormState.specToInput),

		kind:
			spec.plugin.kind === "Markdown"
				? {
						active: "markdown" as const,
						branches: {
							markdown: spec.plugin.spec.text?.source ?? "",
						},
					}
				: {
						active: "metrics" as const,
						branches: {
							metrics: {
								queries: spec.plugin.queries.map(QueryFormState.specToInput),
								settings: SettingsFormState.specToInput(spec.plugin),
							},
						},
					},
	};
}

type FormState = ImpulseFormShape<{
	name: ImpulseFormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
	description: ImpulseFormUnit<string, null, Template<["${", "}"], null>>;
	links: ImpulseFormList<LinkFormState>;
	kind: ImpulseFormSwitch<
		ImpulseFormUnit<"markdown" | "metrics">,
		{
			markdown: ImpulseFormUnit<string, null, Template<["${", "}"], null>>;

			metrics: ImpulseFormShape<{
				queries: ImpulseFormList<QueryFormState>;
				settings: SettingsFormState;
			}>;
		}
	>;
}>;

function outputToSpec({ name, description, links, kind }: GetImpulseFormOutput<FormState>): Spec {
	return DashboardWidgetSpec.of({
		name,
		description,
		plugin:
			kind.kind === "markdown"
				? {
						kind: "Markdown",
						links: links.map(LinkFormState.outputToSpec),
						spec: {
							text: kind.value,
						},
					}
				: {
						...SettingsFormState.outputToSpec(kind.value.settings),
						links: links.map(LinkFormState.outputToSpec),
						queries: kind.value.queries.map(({ promql, alias, minStep }) => ({ query: promql, alias, minStep })),
					},
	});
}

function verboseOutputToSpec({ name, description, links, kind }: GetImpulseFormOutputVerbose<FormState>): Spec {
	return DashboardWidgetSpec.of({
		name: name ?? Template(""),
		description: description ?? Template(""),
		plugin:
			kind.active === "markdown"
				? {
						kind: "Markdown",
						links: links.map(LinkFormState.verboseOutputToSpec),
						spec: {
							text: kind.branches.markdown ?? Template(""),
						},
					}
				: {
						...SettingsFormState.verboseOutputToSpec(kind.branches.metrics.settings),
						links: links.map(LinkFormState.verboseOutputToSpec),
						queries: kind.branches.metrics.queries.map(QueryFormState.verboseOutputToSpec),
					},
	});
}

type Input = ReturnType<typeof specToInput>;

function init(initial?: Input): FormState {
	const queries = initial?.kind?.branches?.metrics?.queries ?? [];

	return ImpulseFormShape({
		name: ImpulseFormUnit(initial?.name ?? "", {
			schema: z
				.string()
				.trim()
				.min(1, "Widget name is required")
				.transform((trimmed) => Template<null>(trimmed)),
			isInputDirty: isStringDirty,
		}),

		description: ImpulseFormUnit(initial?.description ?? "", {
			transform: (input) => Template<null>(input.trim()),
			isInputDirty: isStringDirty,
		}),

		links: ImpulseFormList(initial?.links.map(LinkFormState.init) ?? []),

		kind: ImpulseFormSwitch(ImpulseFormUnit<"markdown" | "metrics">(initial?.kind.active ?? "metrics"), {
			markdown: ImpulseFormUnit(initial?.kind.branches.markdown ?? "", {
				isInputDirty: isStringDirty,
				transform: (input) => Template<null>(input.trim()),
			}),

			metrics: ImpulseFormShape({
				queries: ImpulseFormList(queries.length === 0 ? [QueryFormState.init()] : queries.map(QueryFormState.init)),
				settings: SettingsFormState.init(initial?.kind.branches.metrics?.settings),
			}),
		}),
	});
}

function setInput(input: Input, state: FormState): void {
	batch(() => {
		state.setInput({
			name: input.name,
			description: input.description,
			kind: {
				active: input.kind?.active,
				branches: {
					markdown: input.kind?.branches?.markdown,
				},
			},
		});

		if (input.links) {
			setFormElementsInput((link) => LinkFormState.init(link), input.links.filter(isPresent), state.fields.links);
		}

		if (input.kind?.branches?.metrics?.queries?.length === 0) {
			state.fields.kind.branches.metrics.fields.queries.setElements([QueryFormState.init()]);
		} else if (input.kind?.branches?.metrics?.queries != null) {
			setFormElementsInput(
				(query) => QueryFormState.init(query),
				input.kind.branches.metrics.queries.filter(isPresent),
				state.fields.kind.branches.metrics.fields.queries,
			);
		}

		if (input.kind?.branches?.metrics?.settings != null) {
			SettingsFormState.setInput(
				input.kind.branches.metrics.settings,
				state.fields.kind.branches.metrics.fields.settings,
			);
		}
	});
}

const FormState = {
	init,
	setInput,
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
